// ===========================================
// Invoice Reminder History + "Send reminder now" (2026-09-29)
// ===========================================
// md/plan/invoice-reminders.md Phase 6.4. The invoice's payment reminders (newest first) and a
// button that sends the step the SERVER's preview names — the button never decides a step
// itself. Dates are dd/mm/yyyy: instants in the school's zone, due-date keys with UTC getters.
//
// ⚠️ A send can outlive the proxy timeout (nginx 504 at 60 s) while it still completes, so a
// timeout is reported as "it may still be sending — check the history", and the history is
// refetched after EVERY attempt. Never invite a blind retry of an ambiguous result.

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useInvoiceReminders, useSendInvoiceReminder } from '../../hooks/useInvoices';
import { formatInSchoolTz, formatUTCDate } from '../../utils/dateUtils';
import { apiErrorMessage } from '../../utils/apiError';
import type { InvoiceReminderNextManual, InvoiceReminderRow } from '../../api/invoices.api';

/** "Final notice", or "Overdue reminder (repeat 2)" for the 2nd repeat of a repeating step. */
function stepLabel(step: { stepName: string; occurrence: number | null }): string {
  return step.occurrence && step.occurrence > 0 ? `${step.stepName} (repeat ${step.occurrence})` : step.stepName;
}

const DAY: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** A YYYY-MM-DD key is a calendar day: UTC getters, never the school zone. */
const dayOf = (key: string | null): string => (key ? formatUTCDate(`${key}T00:00:00.000Z`, DAY) : '—');

/** Why a claimed step was not sent, in words. */
function skipText(reason: string | null): string {
  switch (reason) {
    case 'SUPERSEDED':
      return 'skipped: a later reminder went instead';
    case 'ON_HOLD':
      return 'skipped: reminders were on hold';
    case 'PAID_BEFORE_SEND':
      return 'skipped: paid before it went';
    case 'DUE_DATE_CHANGED':
      return 'skipped: the due date changed';
    case 'INVOICE_GONE':
      return 'skipped: the invoice was removed';
    case 'STATUS_CANCELLED':
      // A cancelled invoice is displayed as VOID everywhere.
      return 'skipped: the invoice was voided';
    default:
      return reason?.startsWith('STATUS_')
        ? `skipped: the invoice became ${reason.slice(7).toLowerCase().replace('_', ' ')}`
        : 'skipped';
  }
}

/** A claim older than this with no outcome did not finish (a crash mid-send: see STUCK CLAIMS). */
const STUCK_AFTER_MS = 60 * 60 * 1000;

function OutcomeCell({ row, timeZone, now }: { row: InvoiceReminderRow; timeZone: string; now: number }) {
  const on = (iso: string) => formatInSchoolTz(iso, timeZone, DAY);
  const copyLine = row.copySentTo && (
    <Typography variant="caption" color="text.secondary">Copy to {row.copySentTo}</Typography>
  );
  if (row.sentAt) {
    // A "sent" row that reached nobody is the legitimate zero: no contact email on file.
    if (row.recipientCount === 0) {
      return <Chip size="small" label={`No contact email on file (${on(row.sentAt)})`} />;
    }
    const to = row.recipientCount === 1 ? '1 contact' : `${row.recipientCount} contacts`;
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Chip size="small" color="success" label={`Sent ${on(row.sentAt)} to ${to}`} />
        {copyLine}
      </Stack>
    );
  }
  if (row.failedAt) {
    const label =
      row.recipientCount === 0
        ? `Not delivered ${on(row.failedAt)}`
        : `Only partly delivered ${on(row.failedAt)} (${row.recipientCount} reached)`;
    return (
      <Stack spacing={0.5} alignItems="flex-start">
        <Chip size="small" color="error" label={label} />
        {row.failureReason && (
          <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 260 }}>
            {row.failureReason}
          </Typography>
        )}
        {copyLine}
      </Stack>
    );
  }
  if (row.skippedAt) {
    return <Chip size="small" label={`${skipText(row.skipReason)} (${on(row.skippedAt)})`} />;
  }
  if (now - new Date(row.claimedAt).getTime() > STUCK_AFTER_MS) {
    return <Chip size="small" color="error" label={`Did not finish (started ${on(row.claimedAt)})`} />;
  }
  return <Chip size="small" color="warning" label={`Sending… (started ${on(row.claimedAt)})`} />;
}

interface InvoiceReminderHistoryProps {
  invoiceId: string;
  timeZone: string;
  /** The viewer may send (operations_invoices:write). */
  canSend: boolean;
}

export default function InvoiceReminderHistory({ invoiceId, timeZone, canSend }: InvoiceReminderHistoryProps) {
  const { data, isLoading, isError } = useInvoiceReminders(invoiceId);
  const send = useSendInvoiceReminder();
  const { enqueueSnackbar } = useSnackbar();
  // The step is captured when the dialog OPENS: the preview refetches on window focus, and the
  // dialog must keep naming what the admin agreed to send (/qa Phase 6 LOW-5).
  const [confirmStep, setConfirmStep] = useState<InvoiceReminderNextManual | null>(null);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={22} />
      </Box>
    );
  }
  if (isError || !data) {
    return <Alert severity="error">Could not load the reminder history.</Alert>;
  }

  const next = data.nextManual;
  // ON_HOLD gets a fallback in case the server sends no words with it.
  const blockedText =
    data.nextManualBlockedMessage ??
    (data.nextManualBlockedReason === 'ON_HOLD' ? 'Reminders are on hold for this invoice. Release the hold to send one.' : null);
  const nextLabel = next ? stepLabel(next) : null;

  const onConfirm = () => {
    const expectedStep = confirmStep;
    setConfirmStep(null);
    if (!expectedStep) return;
    send.mutate({ invoiceId, expectedStepId: expectedStep.stepId, expectedOccurrence: expectedStep.occurrence }, {
      onSuccess: (result) => {
        const label = stepLabel(result.step);
        const to = `${result.accepted} contact${result.accepted === 1 ? '' : 's'}`;
        if (result.status === 'SENT' && result.copy === 'FAILED') {
          // /qa Phase 6 LOW-3: the family got it, but the school's copy did not go.
          enqueueSnackbar(`${label} sent to ${to}, but the copy to the billing contact failed.`, { variant: 'warning' });
        } else if (result.status === 'SENT') {
          enqueueSnackbar(`${label} sent to ${to}`, { variant: 'success' });
        } else {
          enqueueSnackbar(
            `${label} reached ${result.accepted} of ${result.recipientCount} contacts. See the history for details.`,
            { variant: 'warning' }
          );
        }
      },
      onError: (error: unknown) => {
        const response = (error as { response?: { status?: number; data?: unknown } })?.response;
        const status = response?.status;
        const serverSaid =
          typeof response?.data === 'object' && response.data !== null && 'message' in response.data;
        // No response, or a 5xx that is NOT our own JSON error (a proxy's 502/504 during a restart,
        // a CDN 52x): the send may still have completed (/qa Phase 6 LOW-1).
        if (!status || (status >= 500 && !serverSaid)) {
          enqueueSnackbar('This is taking a while. It may still be sending, so check the history below before trying again.', {
            variant: 'info',
          });
          return;
        }
        enqueueSnackbar(apiErrorMessage(error, 'Could not send the reminder'), { variant: 'error' });
      },
    });
  };

  return (
    <Stack spacing={1.5}>
      {!data.remindersActive && (
        <Alert severity="info">
          Automatic reminders aren&apos;t switched on yet. You can still send one by hand.
        </Alert>
      )}

      {canSend && (
        <Box>
          <Tooltip title={next ? '' : blockedText ?? ''}>
            {/* span: a disabled button fires no events, so the tooltip needs a wrapper */}
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={send.isPending ? <CircularProgress size={14} /> : <SendIcon />}
                disabled={!next || send.isPending}
                onClick={() => setConfirmStep(next)}
              >
                {send.isPending ? 'Sending…' : nextLabel ? `Send ${nextLabel} now` : 'Send reminder now'}
              </Button>
            </span>
          </Tooltip>
          {!next && blockedText && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {blockedText}
            </Typography>
          )}
        </Box>
      )}

      {data.rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No reminders sent yet.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Reminder</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Scheduled / due</TableCell>
                <TableCell>How</TableCell>
                <TableCell>Outcome</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{stepLabel(row)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {dayOf(row.scheduledFor ?? row.dueDate)}
                    {row.dueDate && row.dueDate !== data.dueDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        before the due date changed
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.trigger === 'MANUAL' ? `Sent by hand by ${row.triggeredByName ?? 'a staff member'}` : 'Automatic'}
                  </TableCell>
                  <TableCell>
                    <OutcomeCell row={row} timeZone={timeZone} now={Date.now()} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      <Dialog open={confirmStep !== null} onClose={() => setConfirmStep(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Send {confirmStep ? stepLabel(confirmStep) : 'the reminder'} now?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It goes to every contact on the family. This step won&apos;t be sent again automatically for
            this occurrence.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmStep(null)}>Cancel</Button>
          <Button variant="contained" onClick={onConfirm} disabled={send.isPending}>
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
